import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast, { Toaster } from "react-hot-toast";
import { AiOutlineLock, AiOutlineMobile, AiOutlineUser } from "react-icons/ai";
import Nav from "../../components/home/Nav";
import AccountList from "../../components/home/AccountList";
import { setUser } from "../../store/reducers/authReducer";
import {
  useChangePasswordMutation,
  useRequestMobileChangeOtpMutation,
  useSetPasswordMutation,
  useUpdateProfileMutation,
  useUserMeQuery,
  useVerifyMobileChangeOtpMutation,
} from "../../store/services/authService";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 60;

const normalizeDigits = (value = "") =>
  value
    .replace(/[۰-۹]/g, (digit) =>
      String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
    )
    .replace(/[٠-٩]/g, (digit) =>
      String("٠١٢٣٤٥٦٧٨٩".indexOf(digit))
    )
    .replace(/\D/g, "");

const toFaDigits = (value = "") =>
  String(value).replace(
    /[0-9]/g,
    (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]
  );

const UserSettings = () => {
  const { user } = useSelector((state) => state.authReducer);
  const dispatch = useDispatch();

  const { data: meData } = useUserMeQuery(
    user?.id || "current-session",
    { refetchOnMountOrArgChange: true }
  );
  const [updateProfile, updateProfileResponse] = useUpdateProfileMutation();
  const [setPassword, setPasswordResponse] = useSetPasswordMutation();
  const [changePassword, changePasswordResponse] = useChangePasswordMutation();
  const [requestMobileOtp, requestMobileResponse] = useRequestMobileChangeOtpMutation();
  const [verifyMobileOtp, verifyMobileResponse] = useVerifyMobileChangeOtpMutation();

  const [name, setName] = useState(user?.name && user.name !== "کاربر جوتی" ? user.name : "");
  const [newMobile, setNewMobile] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [mobileResendIn, setMobileResendIn] = useState(0);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  const hasPassword = Boolean(user?.has_password);
  const profileLoading = updateProfileResponse.isLoading;
  const passwordLoading = setPasswordResponse.isLoading || changePasswordResponse.isLoading;
  const mobileLoading = requestMobileResponse.isLoading || verifyMobileResponse.isLoading;

  useEffect(() => {
    if (meData?.user) {
      dispatch(setUser(meData.user));
      setName(meData.user?.name && meData.user.name !== "کاربر جوتی" ? meData.user.name : "");
    }
  }, [meData?.user, dispatch]);

  useEffect(() => {
    if (mobileResendIn <= 0) {
      return undefined;
    }

    const timer = setInterval(() => {
      setMobileResendIn((prev) =>
        Math.max(prev - 1, 0)
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [mobileResendIn]);

  const saveProfile = async (e) => {
    e.preventDefault();

    try {
      const res = await updateProfile({ name }).unwrap();
      if (res?.user) {
        dispatch(setUser(res.user));
      }
      toast.success(res?.message || "اطلاعات حساب ذخیره شد.");
    } catch (err) {
      toast.error(err?.data?.errors?.name?.[0] || err?.data?.message || "ذخیره اطلاعات انجام نشد.");
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== passwordConfirmation) {
      toast.error("تکرار رمز عبور با رمز جدید یکسان نیست.");
      return;
    }

    try {
      const payload = {
        password: newPassword,
        password_confirmation: passwordConfirmation,
      };

      const res = hasPassword
        ? await changePassword({
            ...payload,
            current_password: currentPassword,
          }).unwrap()
        : await setPassword(payload).unwrap();

      if (res?.user) {
        dispatch(setUser(res.user));
      }

      setCurrentPassword("");
      setNewPassword("");
      setPasswordConfirmation("");
      toast.success(res?.message || "رمز عبور ذخیره شد.");
    } catch (err) {
      toast.error(
        err?.data?.errors?.current_password?.[0] ||
          err?.data?.errors?.password?.[0] ||
          err?.data?.message ||
          "ذخیره رمز عبور انجام نشد."
      );
    }
  };

  const sendMobileCode = async () => {
    if (
      mobileResendIn > 0 ||
      requestMobileResponse.isLoading
    ) {
      return;
    }

    try {
      const res = await requestMobileOtp({
        mobile: newMobile,
      }).unwrap();

      setOtpRequested(true);
      setMobileOtp("");
      setMobileResendIn(
        Number(res?.resend_in) || RESEND_SECONDS
      );

      toast.success(
        res?.message || "کد تایید ارسال شد."
      );
    } catch (err) {
      const retryAfter = Number(
        err?.data?.retry_after || 0
      );

      if (
        Number.isFinite(retryAfter) &&
        retryAfter > 0
      ) {
        setMobileResendIn(
          Math.ceil(retryAfter)
        );
      }

      toast.error(
        err?.data?.errors?.mobile?.[0] ||
          err?.data?.message ||
          "ارسال کد تایید انجام نشد."
      );
    }
  };

  const requestMobileCode = async (e) => {
    e.preventDefault();
    await sendMobileCode();
  };

  const verifyMobileCode = async (e) => {
    e.preventDefault();

    try {
      const res = await verifyMobileOtp({ mobile: newMobile, otp: mobileOtp }).unwrap();
      if (res?.user) {
        dispatch(setUser(res.user));
      }
      setNewMobile("");
      setMobileOtp("");
      setOtpRequested(false);
      setMobileResendIn(0);
      toast.success(res?.message || "شماره موبایل تغییر کرد.");
    } catch (err) {
      toast.error(
        err?.data?.errors?.otp?.[0] ||
          err?.data?.errors?.mobile?.[0] ||
          err?.data?.message ||
          "تایید شماره انجام نشد."
      );
    }
  };

  return (
    <>
      <Nav />
      <Toaster position="top-right" reverseOrder={false} />

      <main className="account-page">
        <div className="my-container">
          <div className="account-shell">
            <AccountList />

            <section className="account-content">
              <div className="account-hero account-hero-compact">
                <div>
                  <span className="account-kicker">تنظیمات حساب</span>
                  <h1>اطلاعات و امنیت</h1>
                  <p>نام، شماره موبایل و رمز عبور حساب را مدیریت کنید.</p>
                </div>
              </div>

              <section className="account-card">
                <div className="account-card-head">
                  <div>
                    <span>اطلاعات کاربری</span>
                    <h2>نام نمایشی</h2>
                  </div>
                  <AiOutlineUser size={25} />
                </div>

                <form className="account-form account-form-two" onSubmit={saveProfile}>
                  <label>
                    <span>نام</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="نام شما" />
                  </label>

                  <button type="submit" disabled={profileLoading}>
                    {profileLoading ? "در حال ذخیره..." : "ذخیره نام"}
                  </button>
                </form>
              </section>

              <section className="account-card">
                <div className="account-card-head">
                  <div>
                    <span>شماره موبایل</span>
                    <h2>تغییر شماره با کد تایید</h2>
                  </div>
                  <AiOutlineMobile size={25} />
                </div>

                <div className="account-current-mobile">
                  شماره فعلی: <strong className="ltr">{user?.mobile || "-"}</strong>
                </div>

                <form className="account-form account-form-two" onSubmit={otpRequested ? verifyMobileCode : requestMobileCode}>
                  <label>
                    <span>شماره جدید</span>
                    <input
                      value={newMobile}
                      onChange={(e) => {
                        setNewMobile(
                          normalizeDigits(
                            e.target.value
                          ).slice(0, 11)
                        );

                        if (otpRequested) {
                          setOtpRequested(false);
                          setMobileOtp("");
                          setMobileResendIn(0);
                        }
                      }}
                      placeholder="09xxxxxxxxx"
                      inputMode="tel"
                    />
                  </label>

                  {otpRequested && (
                    <label>
                      <span>کد تایید</span>
                      <input
                        value={mobileOtp}
                        onChange={(e) =>
                          setMobileOtp(
                            normalizeDigits(
                              e.target.value
                            ).slice(
                              0,
                              OTP_LENGTH
                            )
                          )
                        }
                        placeholder="کد ۴ رقمی"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={OTP_LENGTH}
                      />
                    </label>
                  )}

                  {otpRequested && (
                    <button
                      type="button"
                      onClick={sendMobileCode}
                      disabled={
                        mobileResendIn > 0 ||
                        requestMobileResponse.isLoading
                      }
                    >
                      {requestMobileResponse.isLoading
                        ? "ارسال..."
                        : mobileResendIn > 0
                          ? `ارسال مجدد تا ${toFaDigits(
                              mobileResendIn
                            )} ثانیه دیگر`
                          : "ارسال مجدد کد"}
                    </button>
                  )}

                  <button
                    type="submit"
                    disabled={mobileLoading}
                  >
                    {mobileLoading ? "در حال انجام..." : otpRequested ? "تایید شماره" : "دریافت کد تایید"}
                  </button>
                </form>
              </section>

              <section className="account-card">
                <div className="account-card-head">
                  <div>
                    <span>امنیت حساب</span>
                    <h2>{hasPassword ? "تغییر رمز عبور" : "تعریف رمز عبور"}</h2>
                  </div>
                  <AiOutlineLock size={25} />
                </div>

                <form className="account-form" onSubmit={submitPassword}>
                  {hasPassword && (
                    <label>
                      <span>رمز فعلی</span>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="رمز عبور فعلی"
                      />
                    </label>
                  )}

                  <label>
                    <span>رمز جدید</span>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="حداقل ۸ کاراکتر"
                    />
                  </label>

                  <label>
                    <span>تکرار رمز</span>
                    <input
                      type="password"
                      value={passwordConfirmation}
                      onChange={(e) => setPasswordConfirmation(e.target.value)}
                      placeholder="تکرار رمز جدید"
                    />
                  </label>

                  <button type="submit" disabled={passwordLoading}>
                    {passwordLoading ? "در حال ذخیره..." : hasPassword ? "ذخیره رمز جدید" : "فعال‌سازی رمز عبور"}
                  </button>
                </form>
              </section>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default UserSettings;
